import { Injectable } from '@angular/core';

/**
 * In-memory cache of the latest known Cosmos `_etag` per case id.
 *
 * Populated by `EtagCaptureInterceptor` from each case-detail / actions
 * response (either the body's `_etag` field or the HTTP `ETag` header).
 * Consumed by mutation methods on `CaseApiService` so they can attach
 * `If-Match: <etag>` for optimistic concurrency.
 *
 * On 409 we deliberately wipe the stale entry so the next read will
 * repopulate it.
 */
@Injectable({ providedIn: 'root' })
export class EtagStore {
  private store = new Map<string, string>();

  /** Record an etag for a case id. Empty/null values clear the entry. */
  set(caseId: string, etag: string | null | undefined): void {
    if (!caseId) return;
    if (etag) {
      this.store.set(caseId, etag);
    } else {
      this.store.delete(caseId);
    }
  }

  /** Latest etag we've observed for the case, or `null` if none yet. */
  get(caseId: string): string | null {
    if (!caseId) return null;
    return this.store.get(caseId) ?? null;
  }

  /** Forget the etag — used after a 409 so the next read repopulates it. */
  clear(caseId: string): void {
    if (!caseId) return;
    this.store.delete(caseId);
  }

  /** Wipe everything (useful for tests / sign-out). */
  clearAll(): void {
    this.store.clear();
  }
}
